import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authApi } from '@/services/api';
import { useAuthStore } from '../store/authStore';
import { DEFAULT_AUTHENTICATED_ROUTE } from '@/config/routes';
import { MODAL_COLORS } from '@/components/modal';

/**
 * Handles the redirect from Google OAuth.
 * Reads the `code` query param, exchanges it for tokens via the backend,
 * stores auth state, and navigates to the dashboard.
 */
export default function GoogleCallbackPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuthStore();
  const queryClient = useQueryClient();
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const code = searchParams.get('code');
    if (!code) {
      navigate('/login?error=no_code', { replace: true });
      return;
    }

    const redirectUri = `${window.location.origin}/auth/callback`;

    authApi
      .googleCallback(code, redirectUri, i18n.language)
      .then((response) => {
        const data = response.data;
        if (data.pendingApproval) {
          const name = data.user?.firstName ? `&name=${encodeURIComponent(data.user.firstName)}` : '';
          navigate(`/login?reason=pending_approval${name}`, { replace: true });
          return;
        }
        setAuth(data.user, data.accessToken, data.permissions);
        queryClient.setQueryData(['auth', 'profile'], data.user);
        navigate(DEFAULT_AUTHENTICATED_ROUTE, { replace: true });
      })
      .catch(() => {
        navigate('/login?error=auth_failed', { replace: true });
      });
  }, [searchParams, navigate, setAuth, queryClient]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ backgroundColor: MODAL_COLORS.surface }}
    >
      <div className="text-center space-y-4">
        <div
          className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-t-transparent"
          style={{ borderColor: `${MODAL_COLORS.header} transparent ${MODAL_COLORS.header} ${MODAL_COLORS.header}` }}
        />
        <p className="text-sm font-bold" style={{ color: MODAL_COLORS.text }}>
          {t('auth.authenticating')}
        </p>
      </div>
    </div>
  );
}
